import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Gauge } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { Confirmar } from './ui/Confirmar';

/**
 * MI PLAN — el dashboard de consumo del cliente (panel /procesos).
 * Qué plan rige, cuánto lleva del mes contra sus topes y la puerta de mejora.
 * El superadmin (sin topes) no lo ve: su tablero es /admin.
 */

interface MiPlan {
  plan_codigo: string | null;
  plan_nombre: string;
  ambito: string | null;
  sin_topes: boolean;
  limites: Record<string, unknown>;
  consumo: Record<string, number>;
  vence_el?: string | null;
}

function Barra({ usado, tope, unidad }: { usado: number; tope: unknown; unidad: string }) {
  if (tope === null || tope === undefined) {
    return (
      <p className="font-sans text-xs text-content-muted">
        {usado} {unidad} este mes · sin tope
      </p>
    );
  }
  const t = Number(tope);
  const pct = Math.min(100, Math.round((usado / Math.max(t, 1)) * 100));
  return (
    <div>
      <div className="flex justify-between font-sans text-xs text-content-muted">
        <span>
          {usado} de {t} {unidad}
        </span>
        <span className={pct >= 90 ? 'font-semibold text-error' : ''}>{pct}%</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
        <div
          className={pct >= 90 ? 'h-full rounded-full bg-error' : 'h-full rounded-full bg-primary'}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function MiPlan() {
  const qc = useQueryClient();
  /** Cancelar baja al plan gratuito en el acto: conviene preguntarlo bien. */
  const [porCancelar, setPorCancelar] = useState(false);
  const q = useQuery({
    queryKey: ['mi-plan'],
    queryFn: () => apiFetch<MiPlan>('/pagos/mi-plan'),
    staleTime: 60_000,
  });
  const cancelar = useMutation({
    mutationFn: () => apiFetch<{ detail: string }>('/pagos/cancelar-plan', { method: 'POST' }),
    onSuccess: (r) => {
      window.alert(r.detail);
      void qc.invalidateQueries({ queryKey: ['mi-plan'] });
    },
  });
  const d = q.data;
  if (!d || d.sin_topes || !d.ambito) return null;

  const topeGrab =
    d.limites.grabacion_min_mes ??
    // Los planes chicos topan por sesión, no por mes: mostramos el consumo
    // mensual informativo sin barra de porcentaje.
    null;

  return (
    <section className="rounded-xl border border-border bg-surface-raised p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold text-content">
          <Gauge className="h-4 w-4 text-primary" aria-hidden="true" />
          Plan {d.plan_nombre}
        </h2>
        {d.vence_el && (
          <span className="rounded-full bg-surface-sunken px-2 py-0.5 font-sans text-[11px] text-content-muted">
            activo hasta el {new Date(d.vence_el).toLocaleDateString('es-GT', { day: 'numeric', month: 'short' })}
          </span>
        )}
        <span className="flex-1" />
        <Link
          to="/#planes"
          className="font-sans text-xs font-semibold text-primary hover:underline"
        >
          Cambiar plan
        </Link>
        {d.plan_codigo && d.plan_codigo !== 'voz' && (
          <button
            type="button"
            onClick={() => setPorCancelar(true)}
            className="font-sans text-xs font-semibold text-error hover:underline"
          >
            Cancelar
          </button>
        )}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Barra
          usado={d.consumo.procesos_mes ?? 0}
          tope={d.limites.procesos_mes}
          unidad="procesos"
        />
        <Barra
          usado={d.consumo.grabacion_min_mes ?? 0}
          tope={topeGrab}
          unidad="min de grabación"
        />
        <Barra
          usado={d.consumo.analisis_mes ?? 0}
          tope={d.limites.analisis_mes}
          unidad="análisis con IA"
        />
      </div>
      {d.ambito !== 'personal' && d.limites.facilitadores != null && (
        <div className="mt-3">
          <Barra
            usado={d.consumo.facilitadores ?? 0}
            tope={d.limites.facilitadores}
            unidad="facilitadores"
          />
        </div>
      )}

      <ul className="mt-3 flex flex-wrap gap-1.5">
        {[
          { on: true, txt: `${d.limites.participantes_sesion ?? '∞'} participantes por sesión` },
          {
            on: true,
            txt:
              d.limites.grabacion_min_sesion != null
                ? `${Number(d.limites.grabacion_min_sesion) / 60} h por sesión`
                : 'sin tope por sesión',
          },
          { on: d.limites.familias == null, txt: 'catálogo completo' },
          { on: d.limites.microfono_sala === true, txt: 'micrófono de sala' },
          { on: d.limites.validacion_documentos === true, txt: 'validación de documentos' },
        ].map((c) => (
          <li
            key={c.txt}
            className={
              c.on
                ? 'rounded-full bg-success/10 px-2 py-0.5 font-sans text-[11px] font-medium text-success'
                : 'rounded-full bg-surface-sunken px-2 py-0.5 font-sans text-[11px] text-content-muted line-through'
            }
          >
            {c.txt}
          </li>
        ))}
      </ul>

      <Confirmar
        abierto={porCancelar}
        titulo={`¿Cancelar tu plan ${d.plan_nombre} ahora?`}
        detalle="Pasás de inmediato al plan gratuito. Si tenés suscripción en Recurrente, cancelala también allí para que no se vuelva a cobrar."
        confirmar="Cancelar mi plan"
        onConfirmar={() => {
          setPorCancelar(false);
          cancelar.mutate();
        }}
        onCancelar={() => setPorCancelar(false)}
      />
    </section>
  );
}
